import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { Person } from '@mui/icons-material';
import { MaterialBar } from '../layout/components/MaterialBar.tsx';
import {
    Box,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    useTheme,
} from '@mui/material';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import { DateTime } from 'luxon';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import InfoIcon from '@mui/icons-material/Info';
import {
    currencyCentsToLocalPrice,
    numberToLocalPrice,
    numberToPrice,
} from 'dg-web-shared/lib/NumberFormatter.ts';
import { useNavigate, useParams } from 'react-router-dom';
import {
    RequestMethod,
    RequestStatus,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    Language,
    languageFromString,
    makeLocalizedText,
    Message,
} from 'dg-web-shared/lib/Localized.ts';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    generateProductStateText,
    OperatorProductStatus,
} from './OperatorProductState.tsx';
import React, { type JSX, useEffect, useState } from 'react';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { getOrElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LuxonDateStringFormats } from 'dg-web-shared/lib/LuxonDateStringFormats.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import {
    OperatorParkingaboUsersProductsConcurrentVehiclesLabel,
    OperatorParkingaboUsersProductsTenantRemarkLabel,
    ProductsEnforcementInstructionsLabel,
    productsHasEnforcementInstructions,
} from '../parkingabo-users/OperatorParkingaboUsersProductsAdd.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import {
    guestPermitReceiptUrl,
    GuestPermitValidToDateTimeDropdowns,
    GuestPermitValidToFields,
    GuestPermitValidToFieldValues,
} from '../cloud-connector/CloudConnectorGenerateGuestPermit.tsx';
import { Control, Controller, useForm } from 'react-hook-form';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { DropdownItem } from '../layout/components/ButtonWithDropdown.tsx';
import { ProductTemplateZoneDescriptions } from '../product-types/OperatorProductTypesDetailModal.tsx';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';
import {
    FixedDuration,
    FixedDurationName,
    fixedDurationTextByName,
    mapFixedDurationNameToValue,
    mapFixedDurationValueToName,
} from 'product-shared/product-template/FixedDuration.tsx';
import { OperatorFormSelect } from '../ui/form/OperatorFormSelect.tsx';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { languageOptions } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/CommonOptions.tsx';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import {
    getTypeString,
    LicensePlateType,
} from 'dg-web-shared/dto/LicensePlateType.ts';
import { NoAccessDialog } from '../operator-logins/components/NoAccessDialog.tsx';
import { ParkingaboProductType } from 'product-shared/product-template/ParkingaboProductType.ts';
import { GuestReservationTimeDropdowns } from './OperatorGuestReservationAdd.tsx';
import {
    isSubscriptionProRataEnabled,
    SubscriptionIntervalConfig,
} from 'product-shared/product-template/SubscriptionIntervalConfig.ts';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import {
    ProductTemplateConfig,
    isParkingaboRoleConfigAllRoles,
    ProductTemplateConfigType,
} from 'product-shared/product-template/ProductTemplate.ts';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { CustomerTenantState } from 'product-shared/parkingabo/CustomerTenantState.ts';
import { getAmountAsRappen } from '../api/Http.ts';

export type ProductDetailsType =
    | ProductDetailSubscription
    | ProductDetailReservation
    | ProductDetailPermitUntilRevocation
    | ProductDetailPermitFixedDuration
    | AllAccessBarrierGate
    | GuestPermitValidTo
    | PermitFreeValidTo
    | GuestReservation
    | GuestDayPass;

export interface ProductDetailReservation extends ProductDetails {
    bookedAt: string | null;
    refund: ReservationRefund | null;
    productType:
        | ParkingaboProductType.RESERVATION
        | ParkingaboProductType.GUEST_RESERVATION_UBS;
}

export interface ProductDetailSubscription extends ProductDetails {
    productType: ParkingaboProductType.SUBSCRIPTION;
    bookingData: ProductSubscriptionBooking | null;
    refundData: SubsriptionRefundInfo | null;
    plannedRefundAmount: number | null;
    subscriptionIntervalConfig: SubscriptionIntervalConfig;
    maxRefundableAmount: number;
}

export interface ProductDetailPermitUntilRevocation extends ProductDetails {
    productType: ParkingaboProductType.PERMIT_UNTIL_REVOCATION;
}

export interface ProductDetailPermitFixedDuration extends ProductDetails {
    allowedExtensionDurations: FixedDuration[];
    productType: ParkingaboProductType.PERMIT_FREE_FIXED_DURATION;
}

export interface AllAccessBarrierGate extends ProductDetails {
    productType: ParkingaboProductType.ALL_ACCESS;
}

export interface GuestPermitValidTo extends ProductDetails {
    productType: ParkingaboProductType.GUEST_PERMIT_VALID_TO;
}

export interface PermitFreeValidTo extends ProductDetails {
    productType: ParkingaboProductType.PERMIT_FREE_VALID_TO;
}

export interface GuestReservation extends ProductDetails {
    maxValidTo: string;
    productType: ParkingaboProductType.GUEST_RESERVATION;
}

export interface GuestDayPass extends ProductDetails {
    productType: ParkingaboProductType.GUEST_DAY_PASS;
}

interface ProductDetails {
    data: ProductDetailsData;
    productType: ParkingaboProductType;
}

interface ProductDetailsData {
    contractExternalId: string;
    contractTemplateId: number;
    createdAt: string;
    priceRappen: number | null;
    productState: OperatorProductStatus;
    productName: Message;
    productTemplateConfigType: ProductTemplateConfigType;
    productOwnedBy: string | null;
    productOwnerNr: string | null;
    productOwnerState: CustomerTenantState | null;
    validFrom: string;
    validTo: string | null;
    zoneDescriptions: string[];
    allowedZoneTypes: ZoneType[];
    isStornoBlocked: StornoBlockedReason | null;
    isExtensionBlocked: ExtensionBlockedExplanation | null;
    isTerminateBlocked: RefundBlockedReason | null;
    isRefundBlocked: RefundBlockedReason | null;
    isReactivateBlocked: ReactivateBlockedReason | null;
    productRemark: string | null;
    tenantRemark: string | null;
    enforcementInstructions: string | null;
    productTemplatePaymentModes: ParkingaboCustomerPaymentMode[];
    maxConcurrentVehicles: number | null;
    vehicleTypes: LicensePlateType[];
}

const enum StornoBlockedReason {
    MISSING_PERMISSIONS = 'MISSING_PERMISSIONS',
    ALREADY_PURCHASED = 'ALREADY_PURCHASED',
    WRONG_PRODUCT_STATE = 'WRONG_PRODUCT_STATE',
}

const enum ExtensionBlockedExplanation {
    MISSING_PERMISSIONS = 'MISSING_PERMISSIONS',
    WRONG_PRODUCT_TYPE = 'WRONG_PRODUCT_TYPE',
    WRONG_PRODUCT_STATE = 'WRONG_PRODUCT_STATE',
    PAST_END_DATE = 'PAST_END_DATE',
    END_DATE_TOO_FAR_IN_FUTURE = 'END_DATE_TOO_FAR_IN_FUTURE',
    EXCEEDS_MAXIMUM_DURATION = 'EXCEEDS_MAXIMUM_DURATION',
}

const enum ReactivateBlockedReason {
    MISSING_PERMISSIONS = 'MISSING_PERMISSIONS',
    WRONG_PRODUCT_STATE = 'WRONG_PRODUCT_STATE',
    WRONG_PRODUCT_TYPE = 'WRONG_PRODUCT_TYPE',
    PAST_END_DATE = 'PAST_END_DATE',
    NOT_YET_TERMINATED = 'NOT_YET_TERMINATED',
}

const enum RefundBlockedReason {
    MISSING_PERMISSIONS = 'MISSING_PERMISSIONS',
    ALREADY_REFUNDED = 'ALREADY_REFUNDED',
    WRONG_PRODUCT_TYPE = 'WRONG_PRODUCT_TYPE',
    NO_ACTIVE_ACCOUNT_TO_REFUND = 'NO_ACTIVE_ACCOUNT_TO_REFUND',
    PAST_REFUND_TIME = 'PAST_REFUND_TIME',
    NON_POSITIVE_PRICE = 'NON_POSITIVE_PRICE',
    NO_DIGITAL_PURCHASE = 'NO_DIGITAL_PURCHASE',
    WRONG_PRODUCT_STATE = 'WRONG_PRODUCT_STATE',
    NO_SUBSCRIPTION_CHARGE = 'NO_SUBSCRIPTION_CHARGE',
    SUBSCRIPTION_PRO_RATA_WITH_TERMINATION = 'SUBSCRIPTION_PRO_RATA_WITH_TERMINATION',
}

interface ReservationRefund {
    refundAmountRappen: number;
    refundedAt: string;
    refundedBy: string;
    remark: string;
}

interface ProductSubscriptionBooking {
    totalAmountRappen: number;
    mostRecentAmountRappen: number;
    mostRecentBookedAt: string;
    count: number;
}

interface SubsriptionRefundInfo {
    totalAmountRappen: number;
    mostRecentAmountRappen: number;
    mostRecentRefundedAt: string;
    mostRecentRefundedBy: string;
    mostRecentRemark: string;
    count: number;
}

export function OperatorProductDetailModal({
    backUrl,
    onProductChange,
}: {
    backUrl: string;
    onProductChange: () => void;
}) {
    const { contractExternalId } = useParams<{ contractExternalId: string }>();
    const allowedProductTypes =
        useOperatorContext().currentLogin.allowedProductTypes;
    const [productDetailState, refetchProductDetail] = useServerFetch<
        ProductDetailsType,
        { contractExternalId: string }
    >(
        context => ({
            url: `/ui-api/operator-account/product/${context.contractExternalId}`,
        }),
        contractExternalId ? { contractExternalId } : null,
    );
    const navigate = useNavigate();

    if (!contractExternalId) {
        return null;
    }

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={backUrl}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={productDetailState}
                    render={productDetail => {
                        const hasPermission = allowedProductTypes.includes(
                            productDetail.data.contractTemplateId,
                        );
                        if (hasPermission) {
                            return (
                                <OperatorProductDetail
                                    productDetail={productDetail}
                                    onProductChange={() => {
                                        if (onProductChange) {
                                            onProductChange();
                                        }
                                        refetchProductDetail();
                                    }}
                                    backUrl={backUrl}
                                    onDirtyStateChange={
                                        controller.setShouldConfirmBeforeLeave
                                    }
                                    controlledNavigate={
                                        controller.navigateWithinModal
                                    }
                                />
                            );
                        } else {
                            return (
                                <NoAccessDialog
                                    open
                                    onClose={() => navigate(backUrl)}
                                />
                            );
                        }
                    }}
                />
            )}
        />
    );
}

function MonthlySubscriptionPrice({ priceRappen }: { priceRappen: number }) {
    const formattedPrice = currencyCentsToLocalPrice(priceRappen);
    return (
        <Localized
            de={`${formattedPrice} pro Monat`}
            fr={`${formattedPrice} par mois`}
            it={`${formattedPrice} al mese`}
            en={`${formattedPrice} per month`}
        />
    );
}

type RefundPayload = ReservationRefundPayload | SubscriptionRefundPayload;

interface ReservationRefundPayload {
    contractExternalId: string;
    remark: string;
}

interface SubscriptionRefundPayload extends ReservationRefundPayload {
    subscriptionRefundAmountRappen: number | null;
}

type TerminatePayload =
    | ReservationTerminatePayload
    | ProductDateTerminationPayload
    | PermitFixedDurationTerminatePayload;

interface ReservationTerminatePayload {
    contractExternalId: string;
    reservationRefundRemark: string;
}

interface ProductDateTerminationPayload {
    contractExternalId: string;
    terminateInstantly: boolean;
    terminationDate: DateTime;
    proRataChargeBack: number | null;
}

interface PermitFixedDurationTerminatePayload {
    contractExternalId: string;
}

interface RefundErrorData extends ValidationData {
    error?: RefundError;
}

enum RefundError {
    POSTFINANCE = 'POSTFINANCE',
    NO_PAYMENT_MEAN = 'NO_PAYMENT_MEAN',
    EARLIER_PAYOUT_PENDING = 'EARLIER_PAYOUT_PENDING',
    PAYOUT_DECLINED = 'PAYOUT_DECLINED',
    PAYOUT_UNRESOLVED = 'PAYOUT_UNRESOLVED',
}

const enum ExtendGuestPermitFormFields {
    contractExternalId = 'contractExternalId',
}

type ExtensionPayload =
    | GuestPermitExtensionPayload
    | GuestReservationPayload
    | PermitFixedDurationExtensionPayload
    | PermitFreeValidToExtensionPayload;

interface GuestPermitExtensionPayload extends GuestPermitValidToFieldValues {
    [ExtendGuestPermitFormFields.contractExternalId]: string;
}

const enum GuestReservationFields {
    contractExternalId = 'contractExternalId',
    validToDateTime = 'validToDateTime',
}

interface GuestReservationFieldValues {
    [GuestReservationFields.contractExternalId]: string;
    [GuestReservationFields.validToDateTime]: DateTime | null;
}

interface GuestReservationPayload {
    contractExternalId: string;
    validToDateTime: DateTime;
}

const enum PermitFixedDurationExtensionFormFields {
    contractExternalId = 'contractExternalId',
    extensionDurationName = 'extensionDurationName',
}

interface PermitFixedDurationExtensionFormValues {
    [PermitFixedDurationExtensionFormFields.contractExternalId]: string;
    [PermitFixedDurationExtensionFormFields.extensionDurationName]: FixedDurationName;
}

const enum PermitFreeValidToExtensionFormFields {
    contractExternalId = 'contractExternalId',
    validToDate = 'validToDate',
}

interface PermitFreeValidToExtensionPayload {
    [PermitFreeValidToExtensionFormFields.contractExternalId]: string;
    [PermitFreeValidToExtensionFormFields.validToDate]: DateTime;
}

interface PermitFixedDurationExtensionPayload {
    contractExternalId: string;
    extensionDuration: FixedDuration;
}

const enum ProductRemarkFormFields {
    tenantRemark = 'tenantRemark',
    enforcementInstructions = 'enforcementInstructions',
}

interface ProductRemarkFormValues {
    [ProductRemarkFormFields.tenantRemark]: string | null;
    [ProductRemarkFormFields.enforcementInstructions]: string | null;
}

interface ProductRemarkPayload extends ProductRemarkFormValues {
    contractExternalId: string;
}

function OperatorProductDetail({
    productDetail,
    onProductChange,
    backUrl,
    onDirtyStateChange,
    controlledNavigate,
}: {
    productDetail: ProductDetailsType;
    onProductChange: () => void;
    backUrl: string;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
    controlledNavigate: (url: string) => void;
}) {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { currentLogin, operatorExternalId } = useOperatorContext();
    const permParkingaboUserEdit = currentLogin.permissions.parkingaboUserEdit;

    const [showStornoDialog, setShowStornoDialog] = useState(false);
    const [stornoRequestState, stornoProduct] = useServerWrite<
        { contractExternalId: string },
        null,
        ValidationData
    >(context => ({
        url: `/ui-api/operator-account/product/${context.contractExternalId}/storno`,
    }));
    useServerSuccessEffect(stornoRequestState, () => {
        onProductChange();
    });

    const [showExtensionDialog, setShowExtensionDialog] = useState(false);
    const [extensionRequestState, extendProduct] = useServerWrite<
        ExtensionPayload,
        null,
        ValidationData
    >(context => ({
        url: `/ui-api/operator-account/product/${context.contractExternalId}/extend`,
    }));
    useServerSuccessEffect(extensionRequestState, () => {
        onProductChange();
    });

    const [showTerminateDialog, setShowTerminateDialog] = useState(false);
    const [terminateRequestState, terminateProduct] = useServerWrite<
        TerminatePayload,
        null,
        RefundErrorData
    >(context => ({
        url: `/ui-api/operator-account/product/${context.contractExternalId}/terminate`,
    }));
    useServerSuccessEffect(terminateRequestState, () => {
        onProductChange();
    });

    const [showRefundDialog, setShowRefundDialog] = useState(false);
    const [refundRequestState, refundProduct] = useServerWrite<
        RefundPayload,
        null,
        RefundErrorData
    >(context => ({
        url: `/ui-api/operator-account/product/${context.contractExternalId}/refund`,
    }));
    useServerSuccessEffect(refundRequestState, () => {
        onProductChange();
    });

    const [refundError, setRefundError] = useState<RefundError | null>(null);
    useServerErrorEffect(terminateRequestState, (_, data) =>
        setRefundError(data?.error ?? null),
    );
    useServerErrorEffect(refundRequestState, (_, data) =>
        setRefundError(data?.error ?? null),
    );

    const [showReactivateDialog, setShowReactivateDialog] = useState(false);
    const [reactivateRequestState, reactivateProduct] = useServerWrite<
        { contractExternalId: string },
        null,
        ValidationData
    >(context => ({
        url: `/ui-api/operator-account/product/${context.contractExternalId}/reactivate`,
    }));
    useServerSuccessEffect(reactivateRequestState, () => {
        onProductChange();
    });

    const [updateTenantRemarkRequestState, updateTenantRemark] = useServerWrite<
        ProductRemarkPayload,
        null,
        ValidationData
    >(context => ({
        url: `/ui-api/operator-account/product/${context.contractExternalId}/update-product-remark`,
        method: RequestMethod.PUT,
    }));
    useServerSuccessEffect(updateTenantRemarkRequestState, () => {
        onProductChange();
    });
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm<ProductRemarkFormValues>({
        defaultValues: {
            [ProductRemarkFormFields.tenantRemark]:
                productDetail.data.tenantRemark,
            [ProductRemarkFormFields.enforcementInstructions]:
                productDetail.data.enforcementInstructions,
        },
        mode: 'onBlur',
    });
    useEffect(() => {
        onDirtyStateChange(isDirty);
    }, [isDirty, onDirtyStateChange]);

    const [showGuestCardDownloadDialog, setShowGuestCardDownloadDialog] =
        useState(false);

    const actionIsLoading =
        stornoRequestState.status === RequestStatus.PENDING ||
        terminateRequestState.status === RequestStatus.PENDING ||
        refundRequestState.status === RequestStatus.PENDING ||
        reactivateRequestState.status === RequestStatus.PENDING;

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={<Localized {...productDetail.data.productName} />}
                    Icon={AssignmentIndIcon}
                    editMode={isDirty}
                    buttons={
                        isDirty ? (
                            <HeaderCancelSaveButtons
                                onCancel={reset}
                                onSave={handleSubmit(
                                    (fields: ProductRemarkFormValues) =>
                                        updateTenantRemark({
                                            contractExternalId:
                                                productDetail.data
                                                    .contractExternalId,
                                            tenantRemark: fields.tenantRemark,
                                            enforcementInstructions:
                                                fields.enforcementInstructions,
                                        }),
                                )}
                                loading={
                                    updateTenantRemarkRequestState.status ===
                                    RequestStatus.PENDING
                                }
                            />
                        ) : (
                            <HeaderActionCloseButtons
                                onClose={() => navigate(backUrl)}
                                isLoading={actionIsLoading}
                                dropdownItems={[
                                    {
                                        key: 'storno',
                                        label: (
                                            <Localized
                                                de="Löschen"
                                                fr="Supprimer"
                                                it="Cancella"
                                                en="Delete"
                                            />
                                        ),
                                        tooltip: productDetail.data
                                            .isStornoBlocked ? (
                                            <StornoTooltip
                                                isStornoBlocked={
                                                    productDetail.data
                                                        .isStornoBlocked
                                                }
                                            />
                                        ) : undefined,
                                        disabled:
                                            !!productDetail.data
                                                .isStornoBlocked,
                                        onClickCallback: () =>
                                            setShowStornoDialog(true),
                                    },
                                    {
                                        key: 'extension',
                                        label: (
                                            <Localized
                                                de="Verlängern"
                                                fr="Prolonger"
                                                it="Prolunga"
                                                en="Extend"
                                            />
                                        ),
                                        tooltip: productDetail.data
                                            .isExtensionBlocked ? (
                                            <ExtensionTooltip
                                                isExtensionBlocked={
                                                    productDetail.data
                                                        .isExtensionBlocked
                                                }
                                            />
                                        ) : undefined,
                                        disabled:
                                            !!productDetail.data
                                                .isExtensionBlocked,
                                        onClickCallback: () =>
                                            setShowExtensionDialog(true),
                                    },
                                    {
                                        key: 'terminate',
                                        label: (
                                            <Localized
                                                de="Beenden"
                                                fr="Terminer"
                                                it="Termina"
                                                en="Terminate"
                                            />
                                        ),
                                        tooltip: productDetail.data
                                            .isTerminateBlocked ? (
                                            <TerminateRefundTooltip
                                                blockedReason={
                                                    productDetail.data
                                                        .isTerminateBlocked
                                                }
                                                productType={
                                                    productDetail.productType
                                                }
                                                type="terminate"
                                            />
                                        ) : undefined,
                                        disabled:
                                            !!productDetail.data
                                                .isTerminateBlocked,
                                        onClickCallback: () =>
                                            setShowTerminateDialog(true),
                                    },
                                    {
                                        key: 'reactivate',
                                        label: (
                                            <Localized
                                                de="Reaktivieren"
                                                fr="Réactiver"
                                                it="Riattiva"
                                                en="Reactivate"
                                            />
                                        ),
                                        tooltip: productDetail.data
                                            .isReactivateBlocked ? (
                                            <ReactivateTooltip
                                                isReactivateBlocked={
                                                    productDetail.data
                                                        .isReactivateBlocked
                                                }
                                            />
                                        ) : undefined,
                                        disabled:
                                            !!productDetail.data
                                                .isReactivateBlocked,
                                        onClickCallback: () =>
                                            setShowReactivateDialog(true),
                                    },
                                    { key: 'divider', divider: true },
                                    {
                                        key: 'refund',
                                        label: (
                                            <Localized
                                                de="Zurückerstatten"
                                                fr="Rembourser"
                                                it="Rimborsa"
                                                en="Refund"
                                            />
                                        ),
                                        tooltip: productDetail.data
                                            .isRefundBlocked ? (
                                            <TerminateRefundTooltip
                                                blockedReason={
                                                    productDetail.data
                                                        .isRefundBlocked
                                                }
                                                productType={
                                                    productDetail.productType
                                                }
                                                type="refund"
                                            />
                                        ) : undefined,
                                        disabled:
                                            !!productDetail.data
                                                .isRefundBlocked,
                                        onClickCallback: () =>
                                            setShowRefundDialog(true),
                                    },
                                    ...printGuestProductQrCodeOption(
                                        () =>
                                            setShowGuestCardDownloadDialog(
                                                true,
                                            ),
                                        productDetail,
                                    ),
                                ]}
                            />
                        )
                    }
                />
            }
            bar={
                permParkingaboUserEdit && productDetail.data.productOwnerNr ? (
                    <MaterialBar
                        title={productDetail.data.productOwnedBy}
                        onClick={
                            productDetail.data.productOwnerState !=
                            CustomerTenantState.TERMINATED
                                ? () =>
                                      controlledNavigate(
                                          `/${operatorExternalId}/parkingabo/users/${productDetail.data.productOwnerNr}/detail`,
                                      )
                                : undefined
                        }
                        Icon={Person}
                    />
                ) : (
                    <SeparationBar />
                )
            }
            body={
                <>
                    <StornoDialog
                        open={showStornoDialog}
                        onStorno={() => {
                            setShowStornoDialog(false);
                            stornoProduct({
                                contractExternalId:
                                    productDetail.data.contractExternalId,
                            });
                        }}
                        onCancel={() => setShowStornoDialog(false)}
                        productTemplatePaymentModes={
                            productDetail.data.productTemplatePaymentModes
                        }
                    />
                    <ExtensionDialog
                        open={showExtensionDialog}
                        onCancel={() => setShowExtensionDialog(false)}
                        onExtension={payload => {
                            setShowExtensionDialog(false);
                            extendProduct(payload);
                        }}
                        productDetail={productDetail}
                    />
                    <TerminateDialog
                        open={showTerminateDialog}
                        onTerminate={payload => {
                            setShowTerminateDialog(false);
                            terminateProduct(payload);
                        }}
                        onCancel={() => setShowTerminateDialog(false)}
                        productDetail={productDetail}
                        confirmDisabled={actionIsLoading}
                    />
                    <RefundDialog
                        open={showRefundDialog}
                        onRefund={payload => {
                            setShowRefundDialog(false);
                            refundProduct(payload);
                        }}
                        onCancel={() => setShowRefundDialog(false)}
                        productDetail={productDetail}
                        confirmDisabled={actionIsLoading}
                    />
                    <ReactivateDialog
                        open={showReactivateDialog}
                        onReactivate={() => {
                            setShowReactivateDialog(false);
                            reactivateProduct({
                                contractExternalId:
                                    productDetail.data.contractExternalId,
                            });
                        }}
                        onCancel={() => setShowReactivateDialog(false)}
                    />
                    <GuestCardDownloadDialog
                        open={showGuestCardDownloadDialog}
                        onDownload={() => setShowGuestCardDownloadDialog(false)}
                        onCancel={() => setShowGuestCardDownloadDialog(false)}
                        defaultLanguage={language}
                        operatorId={currentLogin.mandantId}
                        contractExternalId={
                            productDetail.data.contractExternalId
                        }
                    />
                    <BackendRequestErrorMessage
                        requestState={stornoRequestState}
                        sx={{ marginTop: 0, marginX: 0 }}
                    />
                    {terminateRequestState.data?.error === undefined && (
                        <BackendRequestErrorMessage
                            requestState={terminateRequestState}
                            sx={{ marginTop: 0, marginX: 0 }}
                        />
                    )}
                    {refundRequestState.data?.error === undefined && (
                        <BackendRequestErrorMessage
                            requestState={refundRequestState}
                            sx={{ marginTop: 0, marginX: 0 }}
                        />
                    )}
                    <RefundErrorDialog
                        refundError={refundError}
                        onClose={() => setRefundError(null)}
                    />
                    <FormContentLayout>
                        <FormLeftColumn>
                            <TextField
                                disabled
                                label={
                                    <Localized
                                        de="Status"
                                        fr="État"
                                        it="Stato"
                                        en="State"
                                    />
                                }
                                value={makeLocalizedText(
                                    languageFromString(language),
                                )(
                                    generateProductStateText(
                                        productDetail.data.productState,
                                    ),
                                )}
                            />
                            <TextField
                                disabled
                                label={
                                    <Localized
                                        de="Produkt-Typ"
                                        fr="Type de produit"
                                        it="Tipo prodotto"
                                        en="Product type"
                                    />
                                }
                                value={makeLocalizedText(
                                    languageFromString(language),
                                )(productDetail.data.productName)}
                            />

                            <MaxConcurrentVehicles
                                productDetail={productDetail}
                            />

                            <ProductTemplateVehicleTypeField
                                vehicleTypes={productDetail.data.vehicleTypes}
                            />

                            <TextField
                                disabled
                                label={
                                    <Localized
                                        de="Beginn"
                                        fr="Début"
                                        it="Inizio"
                                        en="Start"
                                    />
                                }
                                value={Formatter.dayMonthYearHourMinute(
                                    DateTime.fromISO(
                                        productDetail.data.validFrom,
                                    ),
                                )}
                            />
                            <TextField
                                disabled
                                label={
                                    <Localized
                                        de="Ende"
                                        fr="Fin"
                                        it="Fine"
                                        en="End"
                                    />
                                }
                                value={
                                    productDetail.data.validTo
                                        ? Formatter.dayMonthYearHourMinute(
                                              DateTime.fromISO(
                                                  productDetail.data.validTo,
                                              ),
                                          )
                                        : EmDash
                                }
                            />
                            <ProductTemplateZoneDescriptions
                                zoneDescriptions={
                                    productDetail.data.zoneDescriptions
                                }
                                allowedZoneTypes={
                                    productDetail.data.allowedZoneTypes
                                }
                            />
                        </FormLeftColumn>
                        <OperatorProductRightColumnContainer
                            productDetail={productDetail}
                            control={control}
                        />
                    </FormContentLayout>
                </>
            }
        />
    );
}

export function ProductTemplateVehicleTypeField({
    vehicleTypes,
}: {
    vehicleTypes: LicensePlateType[];
}) {
    const { language } = useLanguage();
    return (
        <ReadOnlyTextField
            label={
                vehicleTypes.length <= 1 ? (
                    <Localized
                        de="Fahrzeugtyp"
                        fr="Type de véhicule"
                        it="Tipo di veicolo"
                        en="Vehicle type"
                    />
                ) : (
                    <Localized
                        de="Fahrzeugtypen"
                        fr="Types de véhicules"
                        it="Tipi di veicolo"
                        en="Vehicle types"
                    />
                )
            }
            value={
                <span style={{ whiteSpace: 'pre-line' }}>
                    {vehicleTypes
                        .map(vehicleType =>
                            getTypeString(language, vehicleType),
                        )
                        .join('\n')}
                </span>
            }
        />
    );
}

export function ProductParkingaboRoleConfigField({
    config,
    roles,
}: {
    config: ProductTemplateConfig;
    roles: Message[] | null;
}) {
    switch (config.configType) {
        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
        case ProductTemplateConfigType.SUBSCRIPTION:
        case ProductTemplateConfigType.CALENDAR_DAY_RESERVATION:
        case ProductTemplateConfigType.GUEST_RESERVATION_UBS:
        case ProductTemplateConfigType.ALL_ACCESS:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
            return (
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Rollen"
                            fr="Rôles"
                            it="Ruoli"
                            en="Roles"
                        />
                    }
                    value={
                        isParkingaboRoleConfigAllRoles(
                            config.parkingaboRoleConfig,
                        ) ? (
                            <Localized
                                de="Alle Rollen"
                                fr="Tous les rôles"
                                it="Tutti i ruoli"
                                en="All roles"
                            />
                        ) : (
                            roles?.map(role => (
                                <div key={role.de}>
                                    <Localized {...role} />
                                </div>
                            ))
                        )
                    }
                />
            );
        case ProductTemplateConfigType.EVENT:
        case ProductTemplateConfigType.CUSTOMER_EXTERNAL:
        case ProductTemplateConfigType.GUEST_PERMIT_VALID_TO:
        case ProductTemplateConfigType.GUEST_RESERVATION:
        case ProductTemplateConfigType.GUEST_DAY_PASS:
            return null;
    }
}

function printGuestProductQrCodeOption(
    onDownloadClick: () => void,
    productDetail: ProductDetailsType,
): DropdownItem[] {
    if (
        ![
            ParkingaboProductType.GUEST_PERMIT_VALID_TO,
            ParkingaboProductType.GUEST_RESERVATION,
            ParkingaboProductType.GUEST_DAY_PASS,
        ].includes(productDetail.productType)
    ) {
        return [];
    }

    const isActiveOrFuture = [
        OperatorProductStatus.ACTIVE,
        OperatorProductStatus.FUTURE,
    ].includes(productDetail.data.productState);
    return [
        { key: 'divider', divider: true },
        {
            key: 'print-qr-code',
            label: (
                <Localized
                    de="Herunterladen"
                    fr="Télécharger"
                    it="Scarica"
                    en="Download"
                />
            ),
            onClickCallback: onDownloadClick,
            disabled: !isActiveOrFuture,
            tooltip: isActiveOrFuture ? undefined : (
                <Localized
                    de="Nur aktive oder zukünftige Produkte können heruntergeladen werden"
                    fr="Seuls les produits actifs ou futurs peuvent être téléchargés"
                    it="Solo i prodotti attivi o futuri possono essere scaricati"
                    en="Only active or future products can be downloaded"
                />
            ),
        },
    ];
}

function OperatorProductRightColumnContainer({
    productDetail,
    control,
}: {
    productDetail: ProductDetailsType;
    control: Control<ProductRemarkFormValues>;
}) {
    return (
        <FormRightColumn>
            <OperatorProductRightColumn productDetail={productDetail} />
            <Controller
                name={ProductRemarkFormFields.tenantRemark}
                control={control}
                render={({ field }) => (
                    <TextField
                        multiline
                        minRows={3}
                        maxRows={3}
                        label={
                            <OperatorParkingaboUsersProductsTenantRemarkLabel />
                        }
                        value={field.value}
                        onChange={field.onChange}
                    />
                )}
            />
            {productsHasEnforcementInstructions(
                productDetail.data.allowedZoneTypes,
                productDetail.data.productTemplateConfigType,
            ) && (
                <Controller
                    name={ProductRemarkFormFields.enforcementInstructions}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={2}
                            label={<ProductsEnforcementInstructionsLabel />}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            )}
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'end',
                }}
            >
                <BlockLabel
                    label={
                        <Localized
                            de="Erstellt am"
                            fr="Créé le"
                            it="Creato il"
                            en="Created at"
                        />
                    }
                >
                    {Formatter.dayMonthYearHourMinute(
                        DateTime.fromISO(productDetail.data.createdAt),
                    )}
                </BlockLabel>
                <BlockLabel label="ID">
                    {productDetail.data.contractExternalId}
                </BlockLabel>
            </Box>
        </FormRightColumn>
    );
}

function OperatorProductRightColumn({
    productDetail,
}: {
    productDetail: ProductDetailsType;
}): JSX.Element | null {
    switch (productDetail.productType) {
        case ParkingaboProductType.SUBSCRIPTION:
            return (
                <OperatorProductRightColumnSubscription
                    productDetail={productDetail}
                />
            );
        case ParkingaboProductType.RESERVATION:
        case ParkingaboProductType.GUEST_RESERVATION_UBS:
            return (
                <OperatorProductRightColumnReservation
                    productDetail={productDetail}
                />
            );
        case ParkingaboProductType.ALL_ACCESS:
        case ParkingaboProductType.GUEST_PERMIT_VALID_TO:
        case ParkingaboProductType.GUEST_RESERVATION:
        case ParkingaboProductType.PERMIT_FREE_FIXED_DURATION:
        case ParkingaboProductType.PERMIT_FREE_VALID_TO:
        case ParkingaboProductType.PERMIT_UNTIL_REVOCATION:
        case ParkingaboProductType.GUEST_DAY_PASS:
            return null;
    }
}

function MaxConcurrentVehicles({
    productDetail,
}: {
    productDetail: ProductDetailsType;
}): JSX.Element | null {
    switch (productDetail.productType) {
        case ParkingaboProductType.PERMIT_UNTIL_REVOCATION:
        case ParkingaboProductType.PERMIT_FREE_FIXED_DURATION:
        case ParkingaboProductType.RESERVATION:
        case ParkingaboProductType.SUBSCRIPTION:
        case ParkingaboProductType.PERMIT_FREE_VALID_TO: {
            if (
                productDetail.data.allowedZoneTypes.some(
                    z => z === ZoneType.barrier_gate,
                )
            ) {
                return (
                    <ReadOnlyTextField
                        label={
                            <OperatorParkingaboUsersProductsConcurrentVehiclesLabel />
                        }
                        value={
                            productDetail.data.maxConcurrentVehicles ?? (
                                <Localized
                                    de="Unbegrenzt"
                                    fr="Illimité"
                                    it="Illimitato"
                                    en="Unlimited"
                                />
                            )
                        }
                    />
                );
            }
            return null;
        }
        case ParkingaboProductType.GUEST_PERMIT_VALID_TO:
        case ParkingaboProductType.GUEST_RESERVATION:
        case ParkingaboProductType.ALL_ACCESS:
        case ParkingaboProductType.GUEST_RESERVATION_UBS:
        case ParkingaboProductType.GUEST_DAY_PASS:
            return null;
    }
}

function OperatorProductRightColumnReservation({
    productDetail,
}: {
    productDetail: ProductDetailReservation;
}) {
    const theme = useTheme();
    return (
        <>
            <TextField
                disabled
                label={
                    <OperatorLabel
                        label={
                            <Localized
                                de="Preis"
                                fr="Prix"
                                it="Prezzo"
                                en="Price"
                            />
                        }
                    />
                }
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                {productDetail.data.priceRappen ? (
                                    <>
                                        {currencyCentsToLocalPrice(
                                            productDetail.data.priceRappen,
                                        )}
                                    </>
                                ) : (
                                    EmDash
                                )}
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                }}
            >
                <Stack
                    spacing={1}
                    style={{
                        flexBasis: '50%',
                        paddingRight: theme.spacing(1),
                    }}
                >
                    <TextField
                        disabled
                        label={
                            <OperatorLabel
                                label={
                                    <Localized
                                        de="Belastung"
                                        fr="Débit"
                                        it="Addebito"
                                        en="Debit"
                                    />
                                }
                                infoText={
                                    productDetail.bookedAt &&
                                    productDetail.data.priceRappen ? (
                                        <PriceTooltip
                                            bookedAt={productDetail.bookedAt}
                                        />
                                    ) : undefined
                                }
                            />
                        }
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {productDetail.bookedAt &&
                                        productDetail.data.priceRappen ? (
                                            <>
                                                {currencyCentsToLocalPrice(
                                                    productDetail.data
                                                        .priceRappen,
                                                )}
                                            </>
                                        ) : (
                                            EmDash
                                        )}
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Stack>
                {productDetail.refund && (
                    <Stack
                        spacing={1}
                        style={{
                            flexBasis: '50%',
                            paddingLeft: theme.spacing(1),
                        }}
                    >
                        <TextField
                            disabled
                            label={
                                <OperatorLabel
                                    label={
                                        <Localized
                                            de="Rückerstattung"
                                            fr="Remboursement"
                                            it="Rimborso"
                                            en="Refund"
                                        />
                                    }
                                    infoText={
                                        productDetail.refund ? (
                                            <ReservationRefundTooltip
                                                refund={productDetail.refund}
                                            />
                                        ) : undefined
                                    }
                                />
                            }
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {productDetail.refund ? (
                                                <>
                                                    {currencyCentsToLocalPrice(
                                                        productDetail.refund
                                                            .refundAmountRappen,
                                                    )}
                                                </>
                                            ) : (
                                                EmDash
                                            )}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Stack>
                )}
            </Box>
            {productDetail.productType ==
                ParkingaboProductType.GUEST_RESERVATION_UBS &&
                productDetail.data.productRemark != null && (
                    <TextField
                        disabled
                        label={
                            <Localized
                                de="Gastdaten"
                                fr="Données du visiteur"
                                it="Dati ospite"
                                en="Guest data"
                            />
                        }
                        value={productDetail.data.productRemark.replace(
                            /\n/g,
                            ' / ',
                        )}
                    />
                )}
        </>
    );
}

function OperatorProductRightColumnSubscription({
    productDetail,
}: {
    productDetail: ProductDetailSubscription;
}) {
    const theme = useTheme();
    return (
        <>
            <TextField
                disabled
                label={
                    <OperatorLabel
                        label={
                            <Localized
                                de="Preis"
                                fr="Prix"
                                it="Prezzo"
                                en="Price"
                            />
                        }
                    />
                }
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                {productDetail.data.priceRappen ? (
                                    <MonthlySubscriptionPrice
                                        priceRappen={
                                            productDetail.data.priceRappen
                                        }
                                    />
                                ) : (
                                    EmDash
                                )}
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                }}
            >
                <Stack
                    spacing={1}
                    style={{
                        flexBasis: '50%',
                        paddingRight: theme.spacing(1),
                    }}
                >
                    <ReadOnlyTextField
                        label={
                            <OperatorLabel
                                label={
                                    <Localized
                                        de="Belastungen"
                                        fr="Débits"
                                        it="Addebiti"
                                        en="Debits"
                                    />
                                }
                                infoText={
                                    productDetail.bookingData ? (
                                        <SubscriptionTooltip
                                            amountRappen={
                                                productDetail.bookingData
                                                    .mostRecentAmountRappen
                                            }
                                            bookedAt={
                                                productDetail.bookingData
                                                    .mostRecentBookedAt
                                            }
                                            refundedBy={null}
                                            remark={null}
                                        />
                                    ) : undefined
                                }
                            />
                        }
                        value={
                            <Box>
                                {productDetail.bookingData ? (
                                    <>
                                        {`${currencyCentsToLocalPrice(
                                            productDetail.bookingData
                                                .totalAmountRappen,
                                        )} (${
                                            productDetail.bookingData.count
                                        })`}
                                    </>
                                ) : (
                                    EmDash
                                )}
                            </Box>
                        }
                    />
                </Stack>
                <Stack
                    spacing={1}
                    style={{
                        flexBasis: '50%',
                        paddingLeft: theme.spacing(1),
                    }}
                >
                    {(productDetail.refundData ||
                        productDetail.plannedRefundAmount) && (
                        <ReadOnlyTextField
                            label={
                                <OperatorLabel
                                    label={
                                        <Localized
                                            de="Rückerstattungen"
                                            fr="Remboursements"
                                            it="Rimborsi"
                                            en="Refunds"
                                        />
                                    }
                                    infoText={
                                        productDetail.refundData ? (
                                            <SubscriptionTooltip
                                                amountRappen={
                                                    productDetail.refundData
                                                        .mostRecentAmountRappen
                                                }
                                                bookedAt={
                                                    productDetail.refundData
                                                        .mostRecentRefundedAt
                                                }
                                                refundedBy={
                                                    productDetail.refundData
                                                        .mostRecentRefundedBy
                                                }
                                                remark={
                                                    productDetail.refundData
                                                        .mostRecentRemark
                                                }
                                            />
                                        ) : undefined
                                    }
                                />
                            }
                            value={
                                <Stack spacing={0.5}>
                                    {productDetail.refundData && (
                                        <Box>
                                            {`${currencyCentsToLocalPrice(
                                                productDetail.refundData
                                                    .totalAmountRappen,
                                            )} (${productDetail.refundData.count})`}
                                        </Box>
                                    )}
                                    {productDetail.plannedRefundAmount && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: theme =>
                                                    theme.palette.warning.light,
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            <Localized
                                                de={`Geplant: ${numberToLocalPrice(productDetail.plannedRefundAmount)}`}
                                                fr={`Prévu: ${numberToLocalPrice(productDetail.plannedRefundAmount)}`}
                                                it={`Previsto: ${numberToLocalPrice(productDetail.plannedRefundAmount)}`}
                                                en={`Scheduled: ${numberToLocalPrice(productDetail.plannedRefundAmount)}`}
                                            />
                                            <Tooltip
                                                title={
                                                    <Localized
                                                        de="Beim Ablauf der Gültigkeit des Produkts wird dieser Betrag (pro rata der bis zum Monatsende verbleibenden ganzen Tage) zurückerstattet."
                                                        fr="À l'expiration de la validité du produit, ce montant (pro rata des jours entiers restants jusqu'à la fin du mois) sera remboursé."
                                                        it="Alla scadenza della validità del prodotto, questo importo (pro rata dei giorni interi restanti fino a fine mese) verrà rimborsato."
                                                        en="Upon expiry of the product's validity, this amount (pro rata of the full days remaining until the end of the month) will be refunded."
                                                    />
                                                }
                                                placement="right"
                                            >
                                                <InfoIcon
                                                    sx={{
                                                        color: theme =>
                                                            theme.palette
                                                                .warning.light,
                                                        marginLeft: 1,
                                                        fontSize: 18,
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Stack>
                            }
                        />
                    )}
                </Stack>
            </Box>
        </>
    );
}

function PriceTooltip({ bookedAt }: { bookedAt: string | null }) {
    return (
        <>
            <b>
                {bookedAt
                    ? Formatter.dayMonthYearHourMinute(
                          DateTime.fromISO(bookedAt),
                      )
                    : EmDash}
            </b>
        </>
    );
}

function ReservationRefundTooltip({ refund }: { refund: ReservationRefund }) {
    return (
        <>
            <b>
                {Formatter.dayMonthYearHourMinute(
                    DateTime.fromISO(refund.refundedAt),
                )}
            </b>
            <br />
            <Localized
                de="Erfasst von"
                fr="Saisi par"
                it="Registrato da"
                en="Captured by"
            />
            : <b>{refund.refundedBy}</b>
            {refund.remark && (
                <>
                    <br />
                    <Localized
                        de="Bemerkung"
                        fr="Remarque"
                        it="Osservazione"
                        en="Remark"
                    />
                    : <b>{refund.remark}</b>
                </>
            )}
        </>
    );
}

function SubscriptionTooltip({
    amountRappen,
    bookedAt,
    refundedBy,
    remark,
}: {
    amountRappen: number;
    bookedAt: string;
    refundedBy: string | null;
    remark: string | null;
}) {
    return (
        <>
            <Localized
                de="Zuletzt am"
                fr="Dernière fois le"
                it="Ultima volta il"
                en="Last time on"
            />
            :{' '}
            <b>
                {Formatter.dayMonthYearHourMinute(DateTime.fromISO(bookedAt))}
            </b>
            <br />
            <Localized
                de="Betrag"
                fr="Montant"
                it="Importo"
                en="Amount"
            />:{' '}
            {currencyCentsToLocalPrice(amountRappen)}
            {refundedBy && (
                <>
                    <br />
                    <Localized
                        de="Erfasst von"
                        fr="Saisi par"
                        it="Registrato da"
                        en="Captured by"
                    />
                    : <b>{refundedBy}</b>
                </>
            )}
            {remark && (
                <>
                    <br />
                    <Localized
                        de="Bemerkung"
                        fr="Remarque"
                        it="Osservazione"
                        en="Remark"
                    />
                    : <b>{remark}</b>
                </>
            )}
        </>
    );
}

function OperatorLabel({
    label,
    infoText,
}: {
    label: React.ReactNode;
    infoText?: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {label}
            {infoText && (
                <Tooltip title={infoText} placement="right">
                    <InfoIcon
                        sx={{
                            color: theme => theme.palette.blue.main,
                            marginLeft: 1,
                        }}
                    />
                </Tooltip>
            )}
        </Box>
    );
}

function StornoDialog({
    open,
    onCancel,
    onStorno,
    productTemplatePaymentModes,
}: {
    open: boolean;
    onCancel: () => void;
    onStorno: () => void;
    productTemplatePaymentModes: ParkingaboCustomerPaymentMode[];
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
            onConfirm={onStorno}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt löschen"
                    fr="Supprimer le produit"
                    it="Eliminare prodotto"
                    en="Delete product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                {productTemplatePaymentModes.includes(
                    ParkingaboCustomerPaymentMode.APP_PAYMENT,
                ) && (
                    <p>
                        <Localized
                            de="Es erfolgt keine Rückerstattung, da das Produkt noch nicht belastet wurde."
                            fr="Aucun remboursement n'aura lieu, car le produit n'a pas encore été débité."
                            it="Non avverrà alcun rimborso, in quanto il prodotto non é ancora stato addebitato."
                            en="No refund will be made as the product has not yet been charged."
                        />
                    </p>
                )}
                <p>
                    <Localized
                        de="Wollen Sie dieses Produkt wirklich löschen?"
                        fr="Voulez-vous vraiment supprimer ce produit?"
                        it="Vuole veramente cancellare questo prodotto?"
                        en="Do you really want to delete this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function ExtensionDialog({
    open,
    onCancel,
    onExtension,
    productDetail,
}: {
    open: boolean;
    onCancel: () => void;
    onExtension: (payload: ExtensionPayload) => void;
    productDetail: ProductDetailsType;
}): JSX.Element | null {
    switch (productDetail.productType) {
        case ParkingaboProductType.SUBSCRIPTION:
        case ParkingaboProductType.ALL_ACCESS:
        case ParkingaboProductType.PERMIT_UNTIL_REVOCATION:
        case ParkingaboProductType.RESERVATION:
        case ParkingaboProductType.GUEST_RESERVATION_UBS:
            return null;
        case ParkingaboProductType.GUEST_DAY_PASS:
        case ParkingaboProductType.GUEST_PERMIT_VALID_TO:
            return (
                <ExtendGuestPermitDialog
                    open={open}
                    onCancel={onCancel}
                    onExtension={onExtension}
                    contractExternalId={productDetail.data.contractExternalId}
                    validTo={productDetail.data.validTo}
                />
            );
        case ParkingaboProductType.GUEST_RESERVATION:
            return (
                <ExtendGuestReservationDialog
                    open={open}
                    onCancel={onCancel}
                    onExtension={onExtension}
                    contractExternalId={productDetail.data.contractExternalId}
                    validTo={productDetail.data.validTo}
                    maxValidTo={productDetail.maxValidTo}
                />
            );
        case ParkingaboProductType.PERMIT_FREE_FIXED_DURATION:
            return (
                <ExtendPermitFixedDurationDialog
                    open={open}
                    onCancel={onCancel}
                    onExtension={onExtension}
                    contractExternalId={productDetail.data.contractExternalId}
                    allowedExtensionDurations={
                        productDetail.allowedExtensionDurations
                    }
                />
            );
        case ParkingaboProductType.PERMIT_FREE_VALID_TO:
            return (
                <ExtendPermitFreeValidtoDialog
                    open={open}
                    onCancel={onCancel}
                    onExtension={onExtension}
                    contractExternalId={productDetail.data.contractExternalId}
                    validTo={productDetail.data.validTo}
                    validFrom={productDetail.data.validFrom}
                />
            );
    }
}

function ExtendGuestPermitDialog({
    open,
    onCancel,
    onExtension,
    contractExternalId,
    validTo,
}: {
    open: boolean;
    onCancel: () => void;
    onExtension: (payload: GuestPermitExtensionPayload) => void;
    contractExternalId: string;
    validTo: string | null;
}) {
    const validToDateTime = validTo
        ? DateTime.fromISO(validTo)
        : DateTime.now();
    const { control, handleSubmit, watch } =
        useForm<GuestPermitExtensionPayload>({
            defaultValues: {
                [ExtendGuestPermitFormFields.contractExternalId]:
                    contractExternalId,
                [GuestPermitValidToFields.validToDate]: validToDateTime.plus({
                    day: 1,
                }),
                [GuestPermitValidToFields.validToHourOfDay]: '23:59',
            },
            mode: 'onBlur',
        });
    const extendedValidToDate = watch(GuestPermitValidToFields.validToDate);
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Verlängern"
                    fr="Prolonger"
                    it="Prolunga"
                    en="Extend"
                />
            }
            onConfirm={handleSubmit(onExtension)}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt verlängern"
                    fr="Prolonger le produit"
                    it="Prolungare il prodotto"
                    en="Extend product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <>
                <p>
                    <GuestPermitValidToDateTimeDropdowns
                        control={control}
                        validToDate={extendedValidToDate}
                        minDateTime={validToDateTime}
                    />
                </p>
                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich verlängern?"
                        fr="Voulez-vous vraiment prolonger ce produit?"
                        it="Vuole davvero prolungare questo prodotto?"
                        en="Do you really want to extend this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function ExtendGuestReservationDialog({
    open,
    onCancel,
    onExtension,
    contractExternalId,
    validTo,
    maxValidTo,
}: {
    open: boolean;
    onCancel: () => void;
    onExtension: (payload: GuestReservationPayload) => void;
    contractExternalId: string;
    validTo: string | null;
    maxValidTo: string;
}) {
    const currentValidTo = validTo ? DateTime.fromISO(validTo) : DateTime.now();
    const { control, handleSubmit } = useForm<GuestReservationFieldValues>({
        defaultValues: {
            [GuestReservationFields.contractExternalId]: contractExternalId,
            [GuestReservationFields.validToDateTime]: null,
        },
        mode: 'onBlur',
    });
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Verlängern"
                    fr="Prolonger"
                    it="Prolunga"
                    en="Extend"
                />
            }
            onConfirm={handleSubmit(data =>
                onExtension({
                    ...data,
                    validToDateTime:
                        data[GuestReservationFields.validToDateTime]!,
                }),
            )}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt verlängern"
                    fr="Prolonger le produit"
                    it="Prolungare il prodotto"
                    en="Extend product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <>
                <p>
                    <GuestReservationTimeDropdowns
                        name={GuestReservationFields.validToDateTime}
                        control={control}
                        minDateTime={currentValidTo.plus({ hour: 1 })}
                        maxDateTime={DateTime.fromISO(maxValidTo)}
                        rules={{
                            required: {
                                value: true,
                                message: mandatoryFieldMessage,
                            },
                        }}
                    />
                </p>
                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich verlängern?"
                        fr="Voulez-vous vraiment prolonger ce produit?"
                        it="Vuole davvero prolungare questo prodotto?"
                        en="Do you really want to extend this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function ExtendPermitFixedDurationDialog({
    open,
    onCancel,
    onExtension,
    contractExternalId,
    allowedExtensionDurations,
}: {
    open: boolean;
    onCancel: () => void;
    onExtension: (payload: PermitFixedDurationExtensionPayload) => void;
    contractExternalId: string;
    allowedExtensionDurations: FixedDuration[];
}) {
    const { control, handleSubmit } =
        useForm<PermitFixedDurationExtensionFormValues>({
            defaultValues: {
                [PermitFixedDurationExtensionFormFields.contractExternalId]:
                    contractExternalId,
                [PermitFixedDurationExtensionFormFields.extensionDurationName]:
                    mapFixedDurationValueToName(allowedExtensionDurations[0])!,
            },
        });
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Verlängern"
                    fr="Prolonger"
                    it="Prolunga"
                    en="Extend"
                />
            }
            onConfirm={handleSubmit(fields =>
                onExtension({
                    contractExternalId: contractExternalId,
                    extensionDuration: mapFixedDurationNameToValue(
                        fields[
                            PermitFixedDurationExtensionFormFields
                                .extensionDurationName
                        ],
                    ),
                }),
            )}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt verlängern"
                    fr="Prolonger le produit"
                    it="Prolungare il prodotto"
                    en="Extend product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <>
                <p>
                    <Localized
                        de={
                            <>
                                Wählen Sie <b>die zusätzliche Dauer</b> des
                                Produkts (das Enddatum wird um die gewählte
                                Dauer zurückgestellt):
                            </>
                        }
                        fr={
                            <>
                                Sélectionnez <b>la durée supplémentaire</b> . du
                                produit (la date de fin sera repoussée de la
                                durée choisie):
                            </>
                        }
                        it={
                            <>
                                Selezioni <b>la durata aggiuntiva</b> del
                                prodotto (la data di fine verrà posticipata
                                della durata selezionata)
                            </>
                        }
                        en={
                            <>
                                Select <b>the additional duration</b>
                                (the end date will be postponed by the selected
                                duration):
                            </>
                        }
                    />
                </p>
                <p>
                    <OperatorFormSelect
                        readonly={allowedExtensionDurations.length === 1}
                        label={
                            <Localized
                                de="Zusätzliche Dauer"
                                fr="Durée supplémentaire"
                                it="Durata aggiuntiva"
                                en="Additional duration"
                            />
                        }
                        name={
                            PermitFixedDurationExtensionFormFields.extensionDurationName
                        }
                        control={control}
                        options={allowedExtensionDurations
                            .map(allowedDuration => {
                                const durationName =
                                    mapFixedDurationValueToName(
                                        allowedDuration,
                                    );
                                if (!durationName) {
                                    return null;
                                }
                                return {
                                    display: (
                                        <Localized
                                            {...fixedDurationTextByName(
                                                durationName,
                                            )}
                                        />
                                    ),
                                    value: durationName,
                                };
                            })
                            .filter(a => a != null)}
                    />
                </p>
                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich verlängern?"
                        fr="Voulez-vous vraiment prolonger ce produit?"
                        it="Vuole davvero prolungare questo prodotto?"
                        en="Do you really want to extend this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function ExtendPermitFreeValidtoDialog({
    open,
    onCancel,
    onExtension,
    contractExternalId,
    validTo,
    validFrom,
}: {
    open: boolean;
    onCancel: () => void;
    onExtension: (payload: PermitFreeValidToExtensionPayload) => void;
    contractExternalId: string;
    validTo: string | null;
    validFrom: string;
}) {
    const currentValidToDate = validTo
        ? DateTime.fromISO(validTo)
        : DateTime.now();
    const { control, handleSubmit } =
        useForm<PermitFreeValidToExtensionPayload>({
            defaultValues: {
                [PermitFreeValidToExtensionFormFields.contractExternalId]:
                    contractExternalId,
                [PermitFreeValidToExtensionFormFields.validToDate]:
                    currentValidToDate.plus({
                        day: 1,
                    }),
            },
            mode: 'onBlur',
        });
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Verlängern"
                    fr="Prolonger"
                    it="Prolunga"
                    en="Extend"
                />
            }
            onConfirm={handleSubmit(onExtension)}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt verlängern"
                    fr="Prolonger le produit"
                    it="Prolungare il prodotto"
                    en="Extend product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <>
                <p>
                    <Controller
                        name={PermitFreeValidToExtensionFormFields.validToDate}
                        control={control}
                        render={({ field, fieldState }) => (
                            <DesktopDatePicker
                                label={
                                    <Localized
                                        de="Ende"
                                        fr="Fin"
                                        it="Fine"
                                        en="End"
                                    />
                                }
                                value={field.value}
                                onChange={field.onChange}
                                minDate={currentValidToDate}
                                maxDate={DateTime.fromISO(validFrom)
                                    .plus({ year: 5 })
                                    .minus({ day: 1 })}
                                slotProps={{
                                    textField: {
                                        error: !!fieldState.error,
                                        helperText: fieldState.error?.message,
                                    },
                                }}
                            />
                        )}
                        rules={{
                            required: {
                                value: true,
                                message: mandatoryFieldMessage,
                            },
                        }}
                    />
                </p>
                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich verlängern?"
                        fr="Voulez-vous vraiment prolonger ce produit?"
                        it="Vuole davvero prolungare questo prodotto?"
                        en="Do you really want to extend this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function ReactivateDialog({
    open,
    onCancel,
    onReactivate,
}: {
    open: boolean;
    onCancel: () => void;
    onReactivate: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Reaktivieren"
                    fr="Réactiver"
                    it="Riattiva"
                    en="Reactivate"
                />
            }
            onConfirm={onReactivate}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt reaktivieren"
                    fr="Réactiver le produit"
                    it="Riattivare prodotto"
                    en="Reactivate product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie dieses Produkt wirklich reaktivieren?"
                    fr="Voulez-vous vraiment réactiver ce produit?"
                    it="Vuole veramente riattivare questo prodotto?"
                    en="Do you really want to reactivate this product?"
                />
            </p>
        </ConfirmationDialog>
    );
}

export function GuestCardDownloadDialog({
    open,
    onCancel,
    onDownload,
    defaultLanguage,
    operatorId,
    contractExternalId,
}: {
    open: boolean;
    onCancel: () => void;
    onDownload: () => void;
    defaultLanguage: Language;
    operatorId: number;
    contractExternalId: string;
}) {
    const { operatorExternalId } = useOperatorContext();
    const [language, setLanguage] = useState<Language>(defaultLanguage);
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Herunterladen"
                    fr="Télécharger"
                    it="Scaricare"
                    en="Download"
                />
            }
            onConfirm={() => {
                window.open(
                    urlWithOperatorExternalId(
                        guestPermitReceiptUrl(operatorId, contractExternalId) +
                            '?language=' +
                            language,
                        operatorExternalId,
                    ),
                    '_blank',
                    'noopener,noreferrer',
                );
                onDownload();
            }}
            onCancel={onCancel}
            title={
                <Localized
                    de="Gastkarte herunterladen"
                    fr="Télécharger la carte d’invité"
                    it="Scarica la carta ospite"
                    en="Download guest card"
                />
            }
            dialogVersion={ConfirmationDialogVersions.PRIMARY}
        >
            <>
                <p>
                    <Localized
                        de="Wählen Sie die gewünschte Sprache für das PDF-Dokument aus."
                        fr="Veuillez sélectionner la langue souhaitée pour le document PDF."
                        it="Selezionare la lingua desiderata per il documento PDF."
                        en="Select the desired language for the PDF document."
                    />
                </p>
                <InputLabel>
                    <Localized
                        de="Sprache"
                        fr="Langue"
                        it="Lingua"
                        en="Language"
                    />
                </InputLabel>
                <Select
                    name={language}
                    value={language}
                    onChange={e => setLanguage(e.target.value as Language)}
                >
                    {languageOptions.map(o => (
                        <MenuItem value={o.value} key={o.value.toString()}>
                            <Localized {...o.translations} />
                        </MenuItem>
                    ))}
                </Select>
            </>
        </ConfirmationDialog>
    );
}

function TerminateDialog({
    open,
    onCancel,
    onTerminate,
    productDetail,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onTerminate: (payload: TerminatePayload) => void;
    productDetail: ProductDetailsType;
    confirmDisabled: boolean;
}): JSX.Element {
    switch (productDetail.productType) {
        case ParkingaboProductType.ALL_ACCESS:
        case ParkingaboProductType.PERMIT_UNTIL_REVOCATION:
        case ParkingaboProductType.PERMIT_FREE_FIXED_DURATION:
            return (
                <ProductTerminateDialogWithDateSelection
                    open={open}
                    onCancel={onCancel}
                    onTerminate={onTerminate}
                    contractExternalId={productDetail.data.contractExternalId}
                    confirmDisabled={confirmDisabled}
                />
            );
        case ParkingaboProductType.SUBSCRIPTION:
            return (
                <ProductTerminateDialogWithDateSelection
                    open={open}
                    onCancel={onCancel}
                    onTerminate={onTerminate}
                    contractExternalId={productDetail.data.contractExternalId}
                    showSubscriptionTerminationInfo
                    subscriptionIntervalConfig={
                        productDetail.subscriptionIntervalConfig
                    }
                    maxRefundableAmount={productDetail.maxRefundableAmount}
                    confirmDisabled={confirmDisabled}
                />
            );
        case ParkingaboProductType.RESERVATION:
        case ParkingaboProductType.GUEST_RESERVATION_UBS:
            return (
                <TerminateDialogReservation
                    open={open}
                    onCancel={onCancel}
                    onTerminate={onTerminate}
                    contractExternalId={productDetail.data.contractExternalId}
                    confirmDisabled={confirmDisabled}
                />
            );
        case ParkingaboProductType.GUEST_PERMIT_VALID_TO:
        case ParkingaboProductType.GUEST_RESERVATION:
        case ParkingaboProductType.PERMIT_FREE_VALID_TO:
        case ParkingaboProductType.GUEST_DAY_PASS:
            return (
                <TerminateProductImmediatelyDialog
                    open={open}
                    onCancel={onCancel}
                    onTerminate={onTerminate}
                    contractExternalId={productDetail.data.contractExternalId}
                    confirmDisabled={confirmDisabled}
                />
            );
    }
}

function TerminateDialogReservation({
    open,
    onCancel,
    onTerminate,
    contractExternalId,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onTerminate: (payload: ReservationTerminatePayload) => void;
    contractExternalId: string;
    confirmDisabled: boolean;
}) {
    const [remark, setRemark] = useState('');
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Beenden"
                    fr="Terminer"
                    it="Termina"
                    en="Terminate"
                />
            }
            onConfirm={() =>
                onTerminate({
                    contractExternalId: contractExternalId,
                    reservationRefundRemark: remark,
                })
            }
            confirmDisabled={confirmDisabled}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt beenden"
                    fr="Terminer le produit"
                    it="Terminare prodotto"
                    en="Terminate product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <p>
                    <Localized
                        de={
                            <>
                                Die Gültigkeit des Produkts wird{' '}
                                <b>sofort beendet</b> und <b>der Gesamtpreis</b>{' '}
                                wird zurückbezahlt.
                            </>
                        }
                        fr={
                            <>
                                La validité du produit sera{' '}
                                <b>terminée immédiatement</b> et{' '}
                                <b>le prix total</b> sera remboursé.
                            </>
                        }
                        it={
                            <>
                                La validità del prodotto verrà{' '}
                                <b>termina immediatamente</b> e{' '}
                                <b>il prezzo totale</b> verrà rimborsato.
                            </>
                        }
                        en={
                            <>
                                The validity of the product will be{' '}
                                <b>immediately terminated</b> and{' '}
                                <b>the total price</b> will be refunded.
                            </>
                        }
                    />
                </p>
                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich beenden?"
                        fr="Voulez-vous vraiment terminer ce produit?"
                        it="Vuole veramente terminare questo prodotto?"
                        en="Do you really want to terminate this product?"
                    />
                </p>
                <TextField
                    autoFocus
                    sx={{ marginBottom: 3 }}
                    label={
                        <Localized
                            de="Bemerkung"
                            fr="Remarque"
                            it="Osservazione"
                            en="Remark"
                        />
                    }
                    type="text"
                    fullWidth
                    value={remark}
                    onChange={event => setRemark(event.target.value)}
                />
            </>
        </ConfirmationDialog>
    );
}

function TerminateProductImmediatelyDialog({
    open,
    onCancel,
    onTerminate,
    contractExternalId,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onTerminate: (payload: PermitFixedDurationTerminatePayload) => void;
    contractExternalId: string;
    confirmDisabled: boolean;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Beenden"
                    fr="Terminer"
                    it="Termina"
                    en="Terminate"
                />
            }
            onConfirm={() =>
                onTerminate({
                    contractExternalId: contractExternalId,
                })
            }
            confirmDisabled={confirmDisabled}
            onCancel={onCancel}
            title={
                <Localized
                    de="Produkt beenden"
                    fr="Terminer le produit"
                    it="Terminare prodotto"
                    en="Terminate product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <p>
                    <Localized
                        de={
                            <>
                                Die Gültigkeit des Produkts wird{' '}
                                <b>sofort beendet</b>.
                            </>
                        }
                        fr={
                            <>
                                La validité du produit sera{' '}
                                <b>terminée immédiatement</b>.
                            </>
                        }
                        it={
                            <>
                                La validità del prodotto verrà{' '}
                                <b>termina immediatamente</b>.
                            </>
                        }
                        en={
                            <>
                                The validity of the product will be{' '}
                                <b>immediately terminated</b>.
                            </>
                        }
                    />
                </p>
                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich beenden?"
                        fr="Voulez-vous vraiment terminer ce produit?"
                        it="Vuole veramente terminare questo prodotto?"
                        en="Do you really want to terminate this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function calculateProRataChargeBack(
    terminationDate: DateTime,
    subscriptionIntervalConfig?: SubscriptionIntervalConfig,
    maxRefundableAmount?: number,
) {
    if (
        !maxRefundableAmount ||
        !subscriptionIntervalConfig ||
        !isSubscriptionProRataEnabled(
            subscriptionIntervalConfig.subscriptionProRataConfig,
        ) ||
        !terminationDate.daysInMonth ||
        terminationDate.day === terminationDate.daysInMonth
    ) {
        return null;
    }
    const now = DateTime.now();
    const monthsBetween =
        (terminationDate.year - now.year) * 12 +
        (terminationDate.month - now.month);
    const maxProRataChargeback =
        monthsBetween * subscriptionIntervalConfig.intervalPrice +
        maxRefundableAmount;

    const remainingDays = terminationDate.daysInMonth - terminationDate.day;
    const calcProRataChargeback =
        subscriptionIntervalConfig.subscriptionProRataConfig.dailyPrice *
        remainingDays;

    return Math.min(maxProRataChargeback, calcProRataChargeback);
}

function ProductTerminateDialogWithDateSelection({
    open,
    onCancel,
    onTerminate,
    contractExternalId,
    showSubscriptionTerminationInfo,
    subscriptionIntervalConfig,
    maxRefundableAmount,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onTerminate: (payload: ProductDateTerminationPayload) => void;
    contractExternalId: string;
    showSubscriptionTerminationInfo?: boolean;
    subscriptionIntervalConfig?: SubscriptionIntervalConfig;
    maxRefundableAmount?: number;
    confirmDisabled: boolean;
}) {
    const [terminateInstantly, setTerminateInstantly] = useState(true);
    const [scheduledTerminationDate, setScheduledTerminationDate] =
        useState<DateTime>(DateTime.now());
    const terminationDate = terminateInstantly
        ? DateTime.now()
        : scheduledTerminationDate;

    const proRataChargeBack = calculateProRataChargeBack(
        terminationDate,
        subscriptionIntervalConfig,
        maxRefundableAmount,
    );
    const subscriptionProRataEnabled = isSubscriptionProRataEnabled(
        subscriptionIntervalConfig?.subscriptionProRataConfig ?? null,
    );

    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Beenden"
                    fr="Terminer"
                    it="Termina"
                    en="Terminate"
                />
            }
            onConfirm={() =>
                onTerminate({
                    contractExternalId: contractExternalId,
                    terminationDate: terminationDate,
                    terminateInstantly: terminateInstantly,
                    proRataChargeBack: proRataChargeBack,
                })
            }
            confirmDisabled={confirmDisabled}
            onCancel={() => {
                onCancel();
                setTerminateInstantly(true);
            }}
            title={
                <Localized
                    de="Produkt beenden"
                    fr="Terminer le produit"
                    it="Termina prodotto"
                    en="Terminate product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <p>
                    <Localized
                        de={
                            <>
                                Wählen Sie <b>das Enddatum der Gültigkeit</b>{' '}
                                des Produkts:
                            </>
                        }
                        fr={
                            <>
                                Sélectionnez <b>la date de fin de validité</b>{' '}
                                du produit:
                            </>
                        }
                        it={
                            <>
                                Selezioni <b>la data di fine validità</b> del
                                prodotto:
                            </>
                        }
                        en={
                            <>
                                Select <b>the end date of the validity</b> of
                                the product:
                            </>
                        }
                    />
                </p>

                <p>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Switch
                            checked={terminateInstantly}
                            onChange={(_, checked) =>
                                setTerminateInstantly(checked)
                            }
                            name="terminateInstantly"
                        />
                        {terminateInstantly && (
                            <Localized
                                de="Sofort"
                                fr="Immédiatement"
                                it="Subito"
                                en="Immediately"
                            />
                        )}
                        {!terminateInstantly && (
                            <>
                                <DesktopDatePicker
                                    format="dd.MM.yyyy"
                                    value={terminationDate}
                                    maxDate={DateTime.now()
                                        .endOf('month')
                                        .plus({ years: 5 })}
                                    minDate={DateTime.now()}
                                    disablePast={true}
                                    onChange={value =>
                                        value !== null &&
                                        setScheduledTerminationDate(value)
                                    }
                                    slotProps={{
                                        textField: { size: 'medium' },
                                    }}
                                />
                                (
                                <Localized
                                    de="um Mitternacht"
                                    fr="à minuit"
                                    it="a mezzanotte"
                                    en="at midnight"
                                />
                                )
                            </>
                        )}
                    </Box>
                </p>

                {showSubscriptionTerminationInfo && (
                    <SubscriptionTerminationInfo
                        terminationDate={terminationDate}
                        terminateInstantly={terminateInstantly}
                        subscriptionProRataEnabled={subscriptionProRataEnabled}
                        proRataChargeBack={proRataChargeBack}
                    />
                )}

                <p>
                    <Localized
                        de="Möchten Sie dieses Produkt wirklich beenden?"
                        fr="Voulez-vous vraiment terminer ce produit?"
                        it="Vuole veramente terminare questo prodotto?"
                        en="Do you really want to terminate this product?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function SubscriptionTerminationInfo({
    terminationDate,
    terminateInstantly,
    subscriptionProRataEnabled,
    proRataChargeBack,
}: {
    terminationDate: DateTime;
    terminateInstantly: boolean;
    subscriptionProRataEnabled: boolean;
    proRataChargeBack: number | null;
}) {
    const lastStartOfMonth = terminationDate
        .startOf('month')
        .toFormat(LuxonDateStringFormats.DATE);

    const terminateInCurrentMonth =
        terminationDate.month === DateTime.local().month;

    const noRefund = !subscriptionProRataEnabled || proRataChargeBack == null;

    return (
        <>
            <p>
                {terminateInCurrentMonth ? (
                    <Localized
                        de="Das Produkt wird nicht mehr belastet."
                        fr="Le produit ne sera plus facturé."
                        it="Il prodotto non sarà più addebitato."
                        en="The product will no longer be charged."
                    />
                ) : (
                    <Localized
                        de={`Die letzte Belastung findet am ${lastStartOfMonth} statt.`}
                        fr={`Le dernier débit sera effectué le ${lastStartOfMonth}.`}
                        it={`L'ultimo addebito avverrà il ${lastStartOfMonth}.`}
                        en={`The last debit will occur on ${lastStartOfMonth}.`}
                    />
                )}
            </p>
            <p>
                <b>
                    {noRefund ? (
                        terminateInstantly ? (
                            <Localized
                                de="Es wird keine Rückerstattung erfolgen."
                                fr="Aucun remboursement ne sera effectué."
                                it="Non sarà effettuato alcun rimborso."
                                en="No refund will be given."
                            />
                        ) : (
                            <Localized
                                de="Beim Ablauf wird keine Rückerstattung erfolgen."
                                fr="Lors de l'expiration, aucun remboursement ne sera effectué."
                                it="Alla scadenza non sarà effettuato alcun rimborso."
                                en="No refunds will be given upon expiry."
                            />
                        )
                    ) : terminateInstantly ? (
                        <Localized
                            de={`Es werden sofort ${numberToPrice(proRataChargeBack)} rückerstattet.`}
                            fr={`Un remboursement de ${numberToPrice(proRataChargeBack)} sera effectué immédiatement.`}
                            it={`Verranno rimborsati subito ${numberToPrice(proRataChargeBack)}.`}
                            en={`${numberToPrice(proRataChargeBack)} will be refunded immediately.`}
                        />
                    ) : (
                        <Localized
                            de={`Beim Ablauf werden ${numberToPrice(proRataChargeBack)} rückerstattet.`}
                            fr={`Lors de l'expiration, un remboursement de ${numberToPrice(proRataChargeBack)} sera effectué.`}
                            it={`Alla scadenza verranno rimborsati ${numberToPrice(proRataChargeBack)}.`}
                            en={`${numberToPrice(proRataChargeBack)} will be refunded upon expiry.`}
                        />
                    )}
                </b>
            </p>
        </>
    );
}

function RefundErrorDialog({
    refundError,
    onClose,
}: {
    refundError: RefundError | null;
    onClose: () => void;
}) {
    return (
        <ConfirmationDialog
            open={refundError !== null}
            confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
            onConfirm={onClose}
            title={
                <Localized
                    de="Rückzahlung nicht möglich"
                    fr="Remboursement impossible"
                    it="Rimborso non possibile"
                    en="Refund not possible"
                />
            }
            dialogVersion={ConfirmationDialogVersions.ERROR}
        >
            <>
                {refundError !== null && (
                    <RefundErrorText refundError={refundError} />
                )}
            </>
        </ConfirmationDialog>
    );
}

function RefundErrorText({
    refundError,
}: {
    refundError: RefundError;
}): JSX.Element {
    switch (refundError) {
        case RefundError.POSTFINANCE:
            return (
                <>
                    <p>
                        <Localized
                            de="Mit PostFinance als Zahlungsmittel sind keine Rückzahlungen möglich."
                            fr="Aucun remboursement n'est possible avec PostFinance comme moyen de paiement."
                            it="Con PostFinance come mezzo di pagamento non sono possibili rimborsi."
                            en="Refunds are not possible with PostFinance as payment mean."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Lassen Sie den Benutzer ein anderes Zahlungsmittel hinterlegen und versuchen Sie es dann erneut."
                            fr="Veuillez demander à l'utilisateur d'enregistrer un autre moyen de paiement, puis réessayez."
                            it="Fate registrare un altro mezzo di pagamento all'utente e poi riprovate."
                            en="Please ask the user to register another payment mean and then try again."
                        />
                    </p>
                </>
            );
        case RefundError.NO_PAYMENT_MEAN:
            return (
                <>
                    <p>
                        <Localized
                            de="Im Parkingabo-Konto von diesem Produkt ist derzeit kein gültiges Zahlungsmittel hinterlegt."
                            fr="Aucun moyen de paiement valide n'est actuellement enregistré sur le compte Parkingabo de ce produit."
                            it="Al momento, nel conto Parkingabo di questo prodotto non c'è nessun mezzo di pagamento valido registrato."
                            en="There is currently no valid payment mean linked to the Parkingabo account of this product."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Lassen Sie den Benutzer zuerst eines hinterlegen und versuchen Sie es dann erneut."
                            fr="Veuillez demander à l'utilisateur d'en enregistrer un au préalable, puis réessayez."
                            it="Fatene prima registrare uno all'utente e poi riprovate."
                            en="Please ask the user to register one first and then try again."
                        />
                    </p>
                </>
            );
        case RefundError.EARLIER_PAYOUT_PENDING:
            return (
                <>
                    <p>
                        <Localized
                            de="Für dieses Produkt ist eine frühere Auszahlung noch offen und wird noch geklärt."
                            fr="Un versement antérieur pour ce produit est encore en suspens et en cours de clarification."
                            it="Un versamento precedente per questo prodotto è ancora in sospeso e in fase di chiarimento."
                            en="An earlier payout for this product is still open and is being clarified."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Bis dahin ist keine weitere Rückzahlung für dieses Produkt möglich."
                            fr="Aucun autre remboursement n'est possible pour ce produit d'ici là."
                            it="Fino ad allora non è possibile alcun altro rimborso per questo prodotto."
                            en="No further refund is possible for this product until then."
                        />
                    </p>
                </>
            );
        case RefundError.PAYOUT_DECLINED:
            return (
                <>
                    <p>
                        <Localized
                            de="Der Zahlungsanbieter hat die Gutschrift auf das hinterlegte Zahlungsmittel abgelehnt. Es wurde nichts zurückerstattet."
                            fr="Le prestataire de paiement a refusé le crédit sur le moyen de paiement enregistré. Aucun montant n'a été remboursé."
                            it="Il fornitore di servizi di pagamento ha rifiutato l'accredito sul mezzo di pagamento registrato. Non è stato rimborsato nulla."
                            en="The payment provider declined the credit to the registered payment mean. Nothing has been refunded."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Lassen Sie den Benutzer das Zahlungsmittel prüfen oder ein anderes hinterlegen und versuchen Sie es dann erneut."
                            fr="Veuillez demander à l'utilisateur de vérifier son moyen de paiement ou d'en enregistrer un autre, puis réessayez."
                            it="Fate verificare all'utente il mezzo di pagamento o fatene registrare un altro e poi riprovate."
                            en="Please ask the user to check the payment mean or register another one and then try again."
                        />
                    </p>
                </>
            );
        case RefundError.PAYOUT_UNRESOLVED:
            return (
                <>
                    <p>
                        <Localized
                            de="Ob die Auszahlung auf das Zahlungsmittel ausgeführt wurde, ist noch offen und wird geklärt."
                            fr="Il reste à clarifier si le versement sur le moyen de paiement a été exécuté."
                            it="Resta da chiarire se il versamento sul mezzo di pagamento sia stato eseguito."
                            en="Whether the payout to the payment mean went through is still open and is being clarified."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Bis dahin ist keine weitere Rückzahlung für dieses Produkt möglich."
                            fr="Aucun autre remboursement n'est possible pour ce produit d'ici là."
                            it="Fino ad allora non è possibile alcun altro rimborso per questo prodotto."
                            en="No further refund is possible for this product until then."
                        />
                    </p>
                </>
            );
    }
}

function RefundDialog({
    open,
    onCancel,
    onRefund,
    productDetail,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onRefund: (payload: RefundPayload) => void;
    productDetail: ProductDetailsType;
    confirmDisabled: boolean;
}) {
    switch (productDetail.productType) {
        case ParkingaboProductType.SUBSCRIPTION:
            if (productDetail.bookingData === null) {
                return null;
            }
            return (
                <RefundDialogSubscription
                    open={open}
                    onCancel={onCancel}
                    onRefund={onRefund}
                    contractExternalId={productDetail.data.contractExternalId}
                    maxRefundableAmount={productDetail.maxRefundableAmount}
                    confirmDisabled={confirmDisabled}
                />
            );
        case ParkingaboProductType.RESERVATION:
        case ParkingaboProductType.GUEST_RESERVATION_UBS:
            return (
                <RefundDialogReservation
                    open={open}
                    onCancel={onCancel}
                    onRefund={onRefund}
                    contractExternalId={productDetail.data.contractExternalId}
                    confirmDisabled={confirmDisabled}
                />
            );
        case ParkingaboProductType.PERMIT_UNTIL_REVOCATION:
        case ParkingaboProductType.PERMIT_FREE_FIXED_DURATION:
        case ParkingaboProductType.ALL_ACCESS:
        case ParkingaboProductType.GUEST_PERMIT_VALID_TO:
        case ParkingaboProductType.GUEST_RESERVATION:
        case ParkingaboProductType.PERMIT_FREE_VALID_TO:
            return null;
    }
}

function RefundDialogReservation({
    open,
    onCancel,
    onRefund,
    contractExternalId,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onRefund: (payload: ReservationRefundPayload) => void;
    contractExternalId: string;
    confirmDisabled: boolean;
}) {
    const [remark, setRemark] = useState('');

    function cancel() {
        setRemark('');
        onCancel();
    }

    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Zurückerstatten"
                    fr="Rembourser"
                    it="Rimborsa"
                    en="Refund"
                />
            }
            onConfirm={() =>
                onRefund({
                    contractExternalId: contractExternalId,
                    remark: remark,
                })
            }
            confirmDisabled={confirmDisabled}
            onCancel={cancel}
            title={
                <Localized
                    de="Produkt zurückerstatten"
                    fr="Rembourser le produit"
                    it="Rimborsa prodotto"
                    en="Refund product"
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <p>
                    <Localized
                        de={
                            <>
                                Möchten Sie wirklich <b>den Gesamtpreis</b>{' '}
                                dieses Produkts zurückerstatten?
                            </>
                        }
                        fr={
                            <>
                                Voulez-vous vraiment rembourser{' '}
                                <b>le prix total</b> de ce produit?
                            </>
                        }
                        it={
                            <>
                                Vuole veramente rimborsare{' '}
                                <b>il prezzo totale</b> di questo prodotto?
                            </>
                        }
                        en={
                            <>
                                Do you really want to refund{' '}
                                <b>the full price</b> of this product?
                            </>
                        }
                    />
                </p>
                <p>
                    <TextField
                        autoFocus
                        sx={{ marginBottom: 3 }}
                        label={
                            <Localized
                                de="Bemerkung"
                                fr="Remarque"
                                it="Osservazione"
                                en="Remark"
                            />
                        }
                        type="text"
                        fullWidth
                        value={remark}
                        onChange={event => setRemark(event.target.value)}
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function RefundDialogSubscription({
    open,
    onCancel,
    onRefund,
    contractExternalId,
    maxRefundableAmount,
    confirmDisabled,
}: {
    open: boolean;
    onCancel: () => void;
    onRefund: (payload: SubscriptionRefundPayload) => void;
    contractExternalId: string;
    maxRefundableAmount: number;
    confirmDisabled: boolean;
}) {
    const [amount, setAmount] = useState('0.00');
    const [remark, setRemark] = useState('');
    const isError = Number(amount) > maxRefundableAmount;
    const noAmount = Number(amount) <= 0;
    const formattedMaxAmount = numberToLocalPrice(maxRefundableAmount);

    function cancel() {
        setRemark('');
        setAmount('0.00');
        onCancel();
    }

    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Produkt zurückerstatten"
                    fr="Rembourser le produit"
                    it="Rimborsa prodotto"
                    en="Refund product"
                />
            }
            confirmLabel={
                <Localized
                    de="Zurückerstatten"
                    fr="Rembourser"
                    it="Rimborsa"
                    en="Refund"
                />
            }
            onConfirm={() =>
                onRefund({
                    contractExternalId: contractExternalId,
                    remark: remark,
                    subscriptionRefundAmountRappen: getAmountAsRappen(
                        Number(amount),
                    ),
                })
            }
            onCancel={cancel}
            confirmDisabled={isError || noAmount || confirmDisabled}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <p>
                    <Localized
                        de="Möchten Sie wirklich den folgenden Betrag zurückerstatten?"
                        fr="Voulez-vous vraiment rembourser le montant suivant?"
                        it="Desideri davvero rimborsare l'importo seguente?"
                        en="Do you really want to refund the following amount?"
                    />
                </p>
                <p>
                    <TextField
                        autoFocus
                        sx={{ marginBottom: 3 }}
                        label={
                            <Localized
                                de="Betrag"
                                fr="Montant"
                                it="Importo"
                                en="Amount"
                            />
                        }
                        type="number"
                        error={isError}
                        helperText={
                            isError ? (
                                <Localized
                                    de={`Der Wert übersteigt den Höchstbetrag (${formattedMaxAmount}).`}
                                    fr={`La valeur dépasse le montant maximum (${formattedMaxAmount}).`}
                                    it={`Il valore supera l'importo massimo (${formattedMaxAmount}).`}
                                    en={`The value exceeds maximum amount (${formattedMaxAmount}).`}
                                />
                            ) : null
                        }
                        fullWidth
                        value={amount}
                        onChange={event =>
                            setAmount(validateRefundInput(event.target.value))
                        }
                        slotProps={{
                            input: {
                                inputProps: {
                                    min: 0,
                                    max: maxRefundableAmount,
                                },
                            },
                        }}
                    />
                </p>
                <p>
                    <TextField
                        autoFocus
                        sx={{ marginBottom: 3 }}
                        label={
                            <Localized
                                de="Bemerkung"
                                fr="Remarque"
                                it="Osservazione"
                                en="Remark"
                            />
                        }
                        type="text"
                        fullWidth
                        value={remark}
                        onChange={event => setRemark(event.target.value)}
                    />
                </p>
            </>
        </ConfirmationDialog>
    );

    function validateRefundInput(value: string) {
        const m = value.match(/(\d+)(([.,])(\d{0,2})?)?/);
        if (!m) {
            return '';
        } else {
            return (
                m[1] + (m[3] || m[4] ? '.' + getOrElse<string>(m[4], '') : '')
            );
        }
    }
}

export function MissingPermissionsText() {
    return (
        <Localized
            de="Funktion nicht verfügbar, da Ihnen die erforderlichen Berechtigungen fehlen"
            fr="Fonction non disponible car vous ne disposez pas des droits nécessaires"
            it="Funzione non disponibile perché le mancano i diritti necessari"
            en="Function not available because you do not have the necessary permissions"
        />
    );
}

function WrongProductTypeText() {
    return (
        <Localized
            de="Funktion nicht verfügbar für diesen Produkttyp"
            fr="Fonction non disponible pour ce type de produit"
            it="Funzione non disponibile per questo tipo di prodotto"
            en="Function not available for this product type"
        />
    );
}

function StornoTooltip({
    isStornoBlocked,
}: {
    isStornoBlocked: StornoBlockedReason;
}): JSX.Element {
    switch (isStornoBlocked) {
        case StornoBlockedReason.MISSING_PERMISSIONS:
            return <MissingPermissionsText />;
        case StornoBlockedReason.ALREADY_PURCHASED:
            return (
                <Localized
                    de="Funktion nur für noch nicht belastete Produkte verfügbar"
                    fr="Fonction uniquement disponible pour produits pas encore débités"
                    it="Funzione disponibile solo per prodotti non ancora addebitati"
                    en="Function only available for products not yet debited"
                />
            );
        case StornoBlockedReason.WRONG_PRODUCT_STATE:
            return (
                <Localized
                    de="Funktion nur für in der Zukunft gültige Produkte verfügbar"
                    fr="Fonction disponible uniquement pour produits valides dans le futur"
                    it="Funzione disponibile solo per prodotti validi in futuro"
                    en="Function only available for products valid in the future"
                />
            );
    }
}

function ExtensionTooltip({
    isExtensionBlocked,
}: {
    isExtensionBlocked: ExtensionBlockedExplanation;
}): JSX.Element {
    switch (isExtensionBlocked) {
        case ExtensionBlockedExplanation.MISSING_PERMISSIONS:
            return <MissingPermissionsText />;
        case ExtensionBlockedExplanation.WRONG_PRODUCT_TYPE:
            return <WrongProductTypeText />;
        case ExtensionBlockedExplanation.PAST_END_DATE:
            return (
                <Localized
                    de="Das Enddatum der Gültigkeit ist bereits erreicht und daher kann das Produkt nicht mehr verlängert werden"
                    fr="La date limite de validité a déjà été atteinte et le produit ne peut donc plus être prolongé"
                    it="La data finale di validità è già stata raggiunta e quindi il prodotto non può più essere prorogato"
                    en="The end date of validity has already been reached and therefore the product can no longer be extended"
                />
            );
        case ExtensionBlockedExplanation.WRONG_PRODUCT_STATE:
            return (
                <Localized
                    de="Funktion nur für aktuell gültige Gastkarten verfügbar"
                    fr="Fonction disponible uniquement pour les cartes d'hôte actuellement valides"
                    it="Funzione disponibile solo per le carte ospiti attualmente valide"
                    en="Function only available for currently valid guest cards"
                />
            );
        case ExtensionBlockedExplanation.END_DATE_TOO_FAR_IN_FUTURE:
            return (
                <Localized
                    de="Das Enddatum ist zu weit in der Zukunft für eine Verlängerung"
                    fr="La date de fin est trop éloignée dans le futur pour une prolongation"
                    it="La data di fine è troppo lontana nel futuro per un'estensione"
                    en="The end date is too far in the future for an extension"
                />
            );
        case ExtensionBlockedExplanation.EXCEEDS_MAXIMUM_DURATION:
            return (
                <Localized
                    de="Das Produkt hat bereits die maximale Laufzeit erreicht und kann daher nicht verlängert werden."
                    fr="Le produit a déjà atteint sa durée maximale et ne peut donc pas être prolongé."
                    it="Il prodotto ha già raggiunto la durata massima e non può quindi essere esteso."
                    en="The product has already reached its maximum duration and therefore cannot be extended."
                />
            );
    }
}

function ReactivateTooltip({
    isReactivateBlocked,
}: {
    isReactivateBlocked: ReactivateBlockedReason;
}): JSX.Element {
    switch (isReactivateBlocked) {
        case ReactivateBlockedReason.MISSING_PERMISSIONS:
            return <MissingPermissionsText />;
        case ReactivateBlockedReason.WRONG_PRODUCT_TYPE:
            return <WrongProductTypeText />;
        case ReactivateBlockedReason.WRONG_PRODUCT_STATE:
            return (
                <Localized
                    de="Funktion nur für aktiv gültige Abonnoments mit Enddatum verfügbar"
                    fr="Fonctionnalité disponible uniquement pour les abonnements actifs avec une date de fin"
                    it="Funzionalità disponibile solo per gli abbonamenti attivi con una data di fine"
                    en="Function only available for active subscriptions with an end date"
                />
            );
        case ReactivateBlockedReason.NOT_YET_TERMINATED:
            return (
                <Localized
                    de="Für dieses Produkt wurde noch kein Enddatum der Gültigkeit festgelegt und es kann daher nicht reaktiviert werden"
                    fr="La date limite de validité n'a pas encore été définie pour ce produit et il ne peut donc pas être réactivé"
                    it="Per questo prodotto non è ancora stata definita una data finale di validità e quindi non può essere riattivato"
                    en="This product has not yet been set an end date of validity and therefore cannot be reactivated"
                />
            );
        case ReactivateBlockedReason.PAST_END_DATE:
            return (
                <Localized
                    de="Das Enddatum der Gültigkeit ist bereits erreicht und daher kann das Produkt nicht mehr reaktiviert werden"
                    fr="La date limite de validité a déjà été atteinte et le produit ne peut donc plus être réactivé"
                    it="La data finale di validità è già stata raggiunta e quindi il prodotto non può più essere riattivato"
                    en="The end date of validity has already been reached and therefore the product can no longer be reactivated"
                />
            );
    }
}

function TerminateRefundTooltip({
    blockedReason,
    productType,
    type,
}: {
    blockedReason: RefundBlockedReason;
    productType: ParkingaboProductType;
    type: 'terminate' | 'refund';
}): JSX.Element {
    switch (blockedReason) {
        case RefundBlockedReason.MISSING_PERMISSIONS:
            return <MissingPermissionsText />;
        case RefundBlockedReason.ALREADY_REFUNDED:
            return (
                <Localized
                    de="Produkt bereits zurückbezahlt"
                    fr="Produit déjà remboursé"
                    it="Prodotto già rimborsato"
                    en="Product already refunded"
                />
            );
        case RefundBlockedReason.WRONG_PRODUCT_TYPE:
            return <WrongProductTypeText />;
        case RefundBlockedReason.NO_ACTIVE_ACCOUNT_TO_REFUND:
            return (
                <Localized
                    de="Rückzahlung nicht möglich: Benutzerkonto gesperrt"
                    fr="Remboursement impossible: compte utilisateur bloqué"
                    it="Rimborso non possibile: conto utente bloccato"
                    en="Refund not possible: user account blocked"
                />
            );
        case RefundBlockedReason.NON_POSITIVE_PRICE:
            return (
                <Localized
                    de="Rückzahlung nicht möglich: Gratisprodukt"
                    fr="Remboursement impossible: produit gratuit"
                    it="Rimborso non possibile: prodotto gratuito"
                    en="Refund not possible: free product"
                />
            );
        case RefundBlockedReason.NO_DIGITAL_PURCHASE:
            return (
                <Localized
                    de="Rückzahlung nicht möglich: es gibt keine Online-Belastung"
                    fr="Remboursement impossible: il n'y a aucun débit en ligne"
                    it="Rimborso non possibile: non esiste alcun addebito online"
                    en="Refund not possible: there is no online charge"
                />
            );
        case RefundBlockedReason.NO_SUBSCRIPTION_CHARGE:
            return (
                <Localized
                    de="Es gibt noch keine Belastungen. Daher ist keine Erstattung mehr möglich."
                    fr="Aucun débit n'a encore été effectué. Aucun remboursement n'est donc possible."
                    it="Non sono stati ancora effettuati addebiti. Pertanto nessun rimborso è possibile."
                    en="There have not been any charges yet. Therefore no refund is possible."
                />
            );
        case RefundBlockedReason.PAST_REFUND_TIME:
            switch (productType) {
                case ParkingaboProductType.RESERVATION:
                    return (
                        <Localized
                            de="Rückzahlung nicht mehr möglich: Produkt älter als 2 Monate"
                            fr="Remboursement n'est plus possible: produit datant de plus de 2 mois"
                            it="Rimborso non più possibile: prodotto più vecchio di 2 mesi"
                            en="Refund no longer possible: product older than 2 months"
                        />
                    );
                case ParkingaboProductType.SUBSCRIPTION:
                    return (
                        <Localized
                            de="Rückzahlung nicht mehr möglich: Produkt älter als 6 Monate"
                            fr="Remboursement n'est plus possible: produit datant de plus de 6 mois"
                            it="Rimborso non più possibile: prodotto più vecchio di 6 mesi"
                            en="Refund no longer possible: product older than 6 months"
                        />
                    );
            }
            throw new Error(
                'Wrong product type should be the refund blocked reason',
            );
        case RefundBlockedReason.WRONG_PRODUCT_STATE:
            switch (type) {
                case 'terminate':
                    return (
                        <Localized
                            de="Funktion nur für aktuell gültige Produkte verfügbar"
                            fr="Fonction disponible uniquement pour les produits actuellement valides"
                            it="Funzione disponibile solo per prodotti attualmente validi"
                            en="Function only available for current valid products"
                        />
                    );
                case 'refund':
                    switch (productType) {
                        case ParkingaboProductType.RESERVATION:
                            return (
                                <Localized
                                    de="Funktion nur für abgelaufene Produkte verfügbar"
                                    fr="Fonction disponible uniquement pour les produits expirés"
                                    it="Funzione disponibile solo per prodotti scaduti"
                                    en="Function only available for expired products"
                                />
                            );
                        case ParkingaboProductType.SUBSCRIPTION:
                            return (
                                <Localized
                                    de="Funktion nur für gültige oder abgelaufene Produkte verfügbar"
                                    fr="Fonction disponible uniquement pour les produits valide ou expirés"
                                    it="Funzione disponibile solo per prodotti valido o scaduti"
                                    en="Function only available for valid or expired products"
                                />
                            );
                    }
            }
            throw new Error(
                'Wrong product type should be the refund blocked reason',
            );
        case RefundBlockedReason.SUBSCRIPTION_PRO_RATA_WITH_TERMINATION:
            return (
                <Localized
                    de="Für Abos mit Rückzahlungsfunktion und bevorstehendem Enddatum können keine Rückzahlungen vorgenommen werden"
                    fr="Aucun remboursement ne peut être effectué pour les abonnements avec fonction de remboursement et une date de fin imminente"
                    it="Non è possibile effettuare rimborsi per abbonamenti con funzione di rimborso e data di scadenza imminente"
                    en="Refunds cannot be issued for subscriptions with a refund feature and an upcoming end date"
                />
            );
    }
}
